import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography
} from '@mui/material';
import axios from 'axios';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { auth } from '../../../api/firebase/config';
import { addPublisherCompany } from '../../../api/firebase/firestore/company';
import { addPublisherUser } from '../../../api/firebase/firestore/user';
import { publisherRegisterSchema } from '../../../api/firebase/firestore/user/validation';
import { sendMailToPublisher } from '../../../api/sendgrid/index';
import {
  black,
  blackText,
  primaryButtonBackground
} from '../../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  titleMb
} from '../../../constants/Location';
import {
  prefecture,
  publisherIndustry,
  sales
} from '../../../constants/Select';
import { select } from '../../../constants/enum';
import { throwError } from '../../../modules/error';
import { sendSlackRegisterPublisher } from '../../../modules/slack';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomRedioButton from '../../uiParts/CustomRadioButton';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';
import PasswordTextFiled from '../../uiParts/PasswordTextField';

import type { AxiosResponse } from 'axios';
import type { DocumentData, DocumentSnapshot } from 'firebase/firestore';

const styles = {
  blackText,
  buttonFont,
  cardContentPadding,
  cardSize,
  textFontSize,
  textMb,
  titleMb,
  zipcodeButton: {
    '&:hover': {
      backgroundColor: black
    },
    backgroundColor: black,
    height: '30px',
    marginBottom: '25px',
    marginLeft: '16px',
    minWidth: '87px'
  }
};

interface PublisherRegisterBasicType {
  companyName: string;
  companyUrl: string;
  industry: number | undefined;
  industryOther: string | undefined;
  representativeEmail: string;
  password: string;
  representativeName: string;
  leaderName: string;
  yearEstablished: number;
  listing: boolean;
  capital: number;
  sales: number;
  numbers: number;
  resources_0: string;
  resources_1: string;
  resources_2: string;
  resources_3: string;
  resources_4: string;
  sns_0: string;
  sns_1: string;
  sns_2: string;
  sns_3: string;
  sns_4: string;
  zipcode: string;
  prefecture: number;
  city: string;
  address: string;
}

interface Address {
  results: [
    {
      address1: string;
      address2: string;
      address3: string;
    }
  ];
}

const PublisherRegisterBasic: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('title.publisher.registerBasic');
  });

  const handleBeforeUnloadEvent = (event: BeforeUnloadEvent): void => {
    event.returnValue = '';
  };

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnloadEvent);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnloadEvent);
  }, []);

  const industryList = publisherIndustry();
  const salesList = sales();

  const [defaultEmail, setDefaultEmail] = useState<string>('');

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm<PublisherRegisterBasicType>({
    defaultValues: {
      listing: false,
      prefecture: 13,
      zipcode: ''
    },
    resolver: yupResolver(publisherRegisterSchema)
  });

  const [firstSubmit, setFirstSubmit] = useState(false);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('companyName') === undefined ||
        '' ||
        watch('companyUrl') === undefined ||
        '' ||
        defaultEmail === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        watch('representativeName') === undefined ||
        '' ||
        watch('yearEstablished') === undefined ||
        '' ||
        watch('listing') === undefined ||
        watch('zipcode') === undefined ||
        '' ||
        watch('prefecture') === undefined ||
        watch('city') === undefined ||
        Boolean(errors.companyName) ||
        Boolean(errors.companyUrl) ||
        Boolean(errors.representativeEmail) ||
        Boolean(errors.password) ||
        Boolean(errors.representativeName) ||
        Boolean(errors.yearEstablished) ||
        Boolean(errors.listing) ||
        Boolean(errors.zipcode) ||
        Boolean(errors.prefecture) ||
        Boolean(errors.city) ||
        Boolean(errors.address) ||
        Boolean(errors.resources_0) ||
        Boolean(errors.resources_1) ||
        Boolean(errors.resources_2) ||
        Boolean(errors.resources_3) ||
        Boolean(errors.resources_4) ||
        Boolean(errors.sns_0) ||
        Boolean(errors.sns_1) ||
        Boolean(errors.sns_2) ||
        Boolean(errors.sns_3) ||
        Boolean(errors.sns_4)
      );
    }
    return false;
  };

  const widthHalf = '50%';
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (params.get('anchor') === 'login') {
        if (!params.get('email')) {
          throw new Error();
        } else {
          setValue(
            'representativeEmail',
            params.get('email')?.split('?').join('') ?? ''
          );
          setDefaultEmail(watch('representativeEmail'));
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      setOpen(true);
      setErrorText(t('registerBasic.linkError'));
      throwError(e);
      setTimeout(() => {
        navigate('/publisher/auth/register');
      }, 6000);
    }
  }, []);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      // AuthUserの作成
      const createdUser = await createUserWithEmailAndPassword(
        auth,
        defaultEmail,
        watch('password')
      );

      if (createdUser) {
        // Companiesコレクションにデータを追加
        const company = await addPublisherCompany(watch);
        // Usersコレクションの更新
        const user = await addPublisherUser(
          params.get('email'),
          company,
          createdUser,
          watch
        );
        const companySnap: DocumentSnapshot<DocumentData> = await getDoc(
          company
        );
        const userSnap: DocumentSnapshot<DocumentData> = await getDoc(user);
        if (companySnap.exists() && userSnap.exists()) {
          const industryFind = industryList.find(
            (element): boolean =>
              element.value === (companySnap.data()?.industry as number)
          )?.option;

          const industry: string | undefined =
            select.other === (companySnap.data()?.industry as number)
              ? (companySnap.data()?.industryOther as string)
              : industryFind;
          // 媒体社slack通知
          sendSlackRegisterPublisher(
            companySnap,
            userSnap,
            `${industry ?? ''}`
          );
          // 媒体社メール通知
          await sendMailToPublisher(userSnap, companySnap, `${industry ?? ''}`);
          // 登録完了画面へ遷移
          navigate('/publisher/auth/complete');
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  const prefectureList = prefecture();
  const getPrefecture = (value: string): number => {
    const data = prefectureList.find(
      (element): boolean => element.option === value
    );
    return data ? data.value : 0;
  };

  const updateZipcodeSub = async (): Promise<void> => {
    try {
      const res: AxiosResponse<Address> = await axios.get(
        'https://zipcloud.ibsnet.co.jp/api/search',
        {
          params: {
            zipcode: watch('zipcode')
          }
        }
      );
      if (res.data.results) {
        const result = res.data.results[0];
        const prefectureNumber: number = getPrefecture(result.address1);
        const city: string = result.address2;
        const address: string = result.address3;
        setValue('prefecture', prefectureNumber);
        setValue('city', city);
        errors.city && clearErrors('city');
        setValue('address', address);
        errors.address && clearErrors('address');
      }
    } catch (e) {
      throwError(e);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Header />
      <Typography component="div" sx={{ marginTop: '24px' }}>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          <Grid>
            <Card sx={styles.cardSize}>
              <CardContent sx={styles.cardContentPadding}>
                <Typography
                  sx={[styles.titleMb, styles.blackText]}
                  component="div"
                >
                  {t('registerBasic.step1')}
                </Typography>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.companyName)}
                      helperText={errors.companyName?.message}
                      labelText={t('registerBasic.companyName')}
                      requiredText={t('form.required')}
                      isRequired
                      placeholder={t('common.enbro')}
                    />
                  )}
                  control={control}
                  name="companyName"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.companyUrl)}
                      helperText={errors.companyUrl?.message}
                      labelText={t('form.companyUrl')}
                      requiredText={t('form.required')}
                      isRequired
                      placeholder="https://en-broker.co.jp"
                      type="url"
                    />
                  )}
                  control={control}
                  name="companyUrl"
                />
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t('common.industryType')}
                      requiredText={t('form.required')}
                      blank
                      defaultValue={0}
                      isRequired={false}
                      list={industryList}
                      sx={{ width: '400px' }}
                      {...(watch('industry') === 99 && { formMb: 'narrow' })}
                    />
                  )}
                  control={control}
                  name="industry"
                />
                {watch('industry') === 99 && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        placeholder={t('form.departmentOther')}
                        sx={{ marginTop: '0px' }}
                      />
                    )}
                    control={control}
                    name="industryOther"
                  />
                )}
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.representativeEmail)}
                      helperText={errors.representativeEmail?.message}
                      disabled
                      labelText={t('registerBasic.representativeEmail')}
                      requiredText={t('form.required')}
                      isRequired
                      type="email"
                      placeholder="xxx@en-broker.co.jp"
                    />
                  )}
                  control={control}
                  name="representativeEmail"
                />
                <Controller
                  render={({ field }) => (
                    <PasswordTextFiled
                      {...field}
                      error={Boolean(errors.password)}
                      helperText={errors.password?.message}
                      labelText={t('form.password')}
                      requiredText={t('form.required')}
                      isRequired
                      fullWidth
                      placeholder=" "
                    />
                  )}
                  control={control}
                  name="password"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.representativeName)}
                      helperText={errors.representativeName?.message}
                      labelText={t('registerBasic.representativeName')}
                      requiredText={t('form.required')}
                      isRequired
                      placeholder={t('common.name')}
                      width={widthHalf}
                    />
                  )}
                  control={control}
                  name="representativeName"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.leaderName)}
                      helperText={errors.leaderName?.message}
                      labelText={t('registerBasic.leaderName')}
                      isRequired={false}
                      placeholder={t('common.name')}
                      width={widthHalf}
                    />
                  )}
                  control={control}
                  name="leaderName"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.yearEstablished)}
                      helperText={errors.yearEstablished?.message}
                      labelText={t('common.yearEstablished')}
                      requiredText={t('form.required')}
                      isRequired
                      placeholder={t('common.yearEx')}
                      width={widthHalf}
                      type="number"
                    />
                  )}
                  control={control}
                  name="yearEstablished"
                />

                <Controller
                  render={({ field }) => (
                    <CustomRedioButton
                      {...field}
                      error={Boolean(errors.listing)}
                      helperText={errors.listing?.message}
                      labelText={t('registerBasic.listingOrNot')}
                      requiredText={t('form.required')}
                      isRequired
                      trueOption={t('registerBasic.listing')}
                      falseOption={t('registerBasic.Notlisting')}
                    />
                  )}
                  control={control}
                  name="listing"
                />

                <Typography sx={{ alignItems: 'center', display: 'flex' }}>
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        error={Boolean(errors.zipcode)}
                        helperText={errors.zipcode?.message}
                        labelText={t('form.zipcode')}
                        requiredText={t('form.required')}
                        isRequired
                        width="narrow"
                        placeholder="0000000"
                        type="number"
                        underText={t('form.zipcodeGuideText')}
                      />
                    )}
                    control={control}
                    name="zipcode"
                  />
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.zipcodeButton}
                    onClick={() => {
                      void (async () => {
                        await updateZipcodeSub();
                      })();
                    }}
                  >
                    {t('form.getAddress')}
                  </Button>
                </Typography>
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      error={Boolean(errors.prefecture)}
                      helperText={errors.prefecture?.message}
                      labelText={t('form.prefecture')}
                      requiredText={t('form.required')}
                      isRequired
                      width="narrow"
                      list={prefecture()}
                      blank
                    />
                  )}
                  control={control}
                  name="prefecture"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.city)}
                      helperText={errors.city?.message}
                      labelText={t('form.city')}
                      requiredText={t('form.required')}
                      isRequired
                      placeholder=" "
                    />
                  )}
                  control={control}
                  name="city"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.address)}
                      helperText={errors.address?.message}
                      labelText={t('form.address')}
                      isRequired={false}
                    />
                  )}
                  control={control}
                  name="address"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.capital)}
                      helperText={errors.capital?.message}
                      labelText={t('registerBasic.capital')}
                      isRequired={false}
                    />
                  )}
                  control={control}
                  name="capital"
                />

                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t('registerBasic.market')}
                      blank
                      defaultValue={0}
                      isRequired={false}
                      list={salesList}
                      sx={{ width: '400px' }}
                    />
                  )}
                  control={control}
                  name="sales"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.numbers)}
                      helperText={errors.numbers?.message}
                      isRequired={false}
                      labelText={t('registerBasic.numbers')}
                      width={widthHalf}
                    />
                  )}
                  control={control}
                  name="numbers"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_0)}
                      helperText={errors.resources_0?.message}
                      labelText={t('registerBasic.resources')}
                      requiredText={t('form.required')}
                      isRequired={false}
                      placeholder=" "
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="resources_0"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_1)}
                      helperText={errors.resources_1?.message}
                      isRequired={false}
                      placeholder=" "
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="resources_1"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_2)}
                      helperText={errors.resources_2?.message}
                      isRequired={false}
                      placeholder=" "
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="resources_2"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_3)}
                      helperText={errors.resources_3?.message}
                      isRequired={false}
                      placeholder=" "
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="resources_3"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.resources_4)}
                      helperText={errors.resources_4?.message}
                      isRequired={false}
                      placeholder=" "
                      type="url"
                    />
                  )}
                  control={control}
                  name="resources_4"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_0)}
                      helperText={errors.sns_0?.message}
                      labelText={t('registerBasic.accounts')}
                      requiredText={t('form.required')}
                      isRequired={false}
                      placeholder="facebook"
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="sns_0"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_1)}
                      helperText={errors.sns_1?.message}
                      isRequired={false}
                      placeholder="Twitter"
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="sns_1"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_2)}
                      helperText={errors.sns_2?.message}
                      isRequired={false}
                      placeholder="Instagram"
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="sns_2"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_3)}
                      helperText={errors.sns_3?.message}
                      isRequired={false}
                      placeholder="TikTok"
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="sns_3"
                />
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      error={Boolean(errors.sns_4)}
                      helperText={errors.sns_4?.message}
                      isRequired={false}
                      placeholder="note"
                      formMb="narrow"
                      type="url"
                    />
                  )}
                  control={control}
                  name="sns_4"
                />
                <CardActions
                  sx={{
                    '&.MuiCardActions-root': {
                      marginTop: '30px'
                    },
                    padding: 0
                  }}
                >
                  <Grid container direction="column" alignItems="center">
                    <Button
                      disabled={buttonDisabledFlag()}
                      variant="contained"
                      sx={[
                        primaryButtonBackground,
                        {
                          '@media screen and (max-width: 960px)': {
                            height: '42px'
                          },
                          width: '100%'
                        }
                      ]}
                      onClick={() => {
                        void (async () => {
                          setFirstSubmit(true);
                          await handleSubmit(async () => await onSubmit())();
                        })();
                      }}
                    >
                      <Typography
                        component="div"
                        color="white"
                        sx={{ fontSize: 15 }}
                      >
                        {t('registerBasic.toMediaRegister')}
                      </Typography>
                    </Button>
                  </Grid>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Typography>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default PublisherRegisterBasic;
